import React, { useRef, useState } from 'react';
import JSZip from 'jszip';
import { TrashIcon } from '../icons/trash.icon';
import { FileIcon } from '../icons/file.icon';
import { useTranslation } from 'react-i18next';
import { ToastService } from 'services/toast.service';
import { CommonDocumentService } from '@umanforge/common';

export type FileOrDir = { file: File, isFile: boolean, isDirectory: boolean };

interface SendFileInputProps {
  label?: string;
  disable?: boolean;
  multipleFiles?: boolean;
  acceptedFormats?: string[];
  onFilesChange?: ( files: FileOrDir[] ) => void;
  onUploadComplete?: ( avatarUrl: string | null ) => void;
  soloFile?: boolean;
  initialUrl?: string | null;
  onInitialRemove?: () => void;
  acceptFolders?: boolean;
}


type LocalFileSystemEntry = LocalFileSystemFileEntry | LocalFileSystemDirectoryEntry;

interface LocalFileSystemFileEntry {
  isFile: true;
  isDirectory: false;
  name: string;
  file( success: ( file: File ) => void ): void;
}

interface LocalFileSystemDirectoryEntry {
  isFile: false;
  isDirectory: true;
  name: string;
  createReader(): LocalFileSystemDirectoryReader;
}

interface LocalFileSystemDirectoryReader {
  readEntries( success: ( entries: LocalFileSystemEntry[] ) => void ): void;
}

const SendFileInput: React.FC<SendFileInputProps> = props => {
  const {
    label,
    disable = false,
    multipleFiles = true,
    acceptedFormats = [],
    soloFile = false,
    acceptFolders = false,
    onFilesChange,
  } = props;

  const { t } = useTranslation();
  const toastService = new ToastService( 'SendFileInput' );

  const [ files, setFiles ] = useState<FileOrDir[]>( [] );
  const [ isDragging, setIsDragging ] = useState( false );
  const inputRef = useRef<HTMLInputElement>( null );

  const MAX_FILE_SIZE_MB = 100000;

  const isFileSizeValid = ( file: File ): boolean => {
    const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;

    if ( !file || file.size === 0 ) {
      toastService.showError( 'translation:errors.file_too_large', {
        max: MAX_FILE_SIZE_MB,
      } );
      return false;
    }

    if ( file.size > maxBytes ) {
      toastService.showError( 'translation:errors.file_too_large', {
        max: MAX_FILE_SIZE_MB,
      } );
      return false;
    }

    return true;
  };

  const isLocalFileEntry = ( e: unknown ): e is LocalFileSystemFileEntry => {
    return (
      typeof e === 'object' &&
      e !== null && 'isFile' in e &&
      ( e as LocalFileSystemFileEntry ).isFile === true &&
      'file' in e &&
      typeof ( e as LocalFileSystemFileEntry ).file === 'function'
    );
  };

  const isLocalDirectoryEntry = ( e: unknown ): e is LocalFileSystemDirectoryEntry => {
    return (
      typeof e === 'object' &&
      e !== null &&
      'isDirectory' in e &&
      ( e as LocalFileSystemDirectoryEntry ).isDirectory === true &&
      'createReader' in e &&
      typeof ( e as LocalFileSystemDirectoryEntry ).createReader === 'function'
    );
  };

  async function extractFilesFromEntry( entry: FileSystemEntry | LocalFileSystemEntry, path = '' ): Promise<{ file: File; path: string }[]> {

    if ( isLocalFileEntry( entry ) ) {
      const file = await new Promise<File>( resolve => {
        return entry.file( resolve );
      } );

      return [ { file, path: path + file.name } ];
    }

    if ( isLocalDirectoryEntry( entry ) ) {
      const reader = entry.createReader();
      const results: { file: File; path: string }[] = [];

      while ( true ) {
        const entries = await new Promise<LocalFileSystemEntry[]>( resolve => {
          return reader.readEntries( resolve );
        } );

        if ( entries.length === 0 ) {
          break;
        }

        const children = await Promise.all(
          entries.map( e =>
          {return extractFilesFromEntry( e, path + entry.name + '/' );}
          )
        );

        results.push( ...children.flat() );
      }

      return results;
    }

    return [];
  }

  const handleDrop = async ( e: React.DragEvent<HTMLDivElement> ) => {
    e.preventDefault();
    setIsDragging( false );

    if ( disable ) {return;}

    const items = [ ...e.dataTransfer.items ]
      .map( item => { return { item: item, entry: item.webkitGetAsEntry() }; } );

    const newFiles: FileOrDir[] = items
      .filter( item => { return item.entry != null && item.entry.isFile; } )
      .map( item => { return { entry: item.entry, file: item.item.getAsFile() }; } )
      .filter( item => { return item.file != null; } )
      .map( item => { return { file: item.file, isFile: true, isDirectory: false }; } ) as FileOrDir[];

    if ( acceptFolders ) {
      const folders = items
        .filter( item => { return item.entry != null && item.entry.isDirectory; } ) as { item: DataTransferItem; entry: FileSystemEntry; }[];

      const results = await Promise.all(
        folders.map( async item => {
          return {
            item: item.item,
            entry: item.entry,
            extractedFiles: await extractFilesFromEntry( item.entry )
          };
        } )
      );

      const zip = new JSZip();
      for ( const result of results ) {
        for ( const f of result.extractedFiles ) {
          zip.file( f.path, f.file );
        }

        const zipFile = new File(
          [ await zip.generateAsync( { type: 'blob' } ) ],
          result.entry.name + '.zip',
          { type: 'application/zip', }
        );

        newFiles.push( { file: zipFile, isFile: false, isDirectory: true } );
      }
    }

    const next: FileOrDir[] = [];
    if ( soloFile ) {
      if ( newFiles.length > 0 ) {
        const file = newFiles[ 0 ];
        next.push( file );
      }
    } else if ( !soloFile ) {
      next.push( ...files, ...newFiles );
    }

    setFiles( next );
    onFilesChange?.( next );
  };

  const handleDragOver = ( e: React.DragEvent<HTMLDivElement> ) => {
    e.preventDefault();
  };

  const handleDragEnter = () => {
    if ( !disable ) {setIsDragging( true );}
  };

  const handleDragLeave = () => {
    setIsDragging( false );
  };

  const handleChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    if ( !e.target.files ) {return;}

    const selectedFiles = Array.from( e.target.files );

    const validFiles = selectedFiles
      .filter( file => {
        const isTypeOk = (
          acceptedFormats.length === 0 ||
          acceptedFormats.includes( file.type )
        );
        const isSizeOk = isFileSizeValid( file );

        return isTypeOk && isSizeOk;
      } )
      .map( item => { return { file: item, isFile: true, isDirectory: false }; } );

    const next = soloFile
      ? [ validFiles[ 0 ] ]
      : [ ...files, ...validFiles ];

    setFiles( next );
    onFilesChange?.( next );

    if ( inputRef.current ) {inputRef.current.value = '';}
  };

  const handleRemove = ( index: number ) => {
    const next = files.filter( ( _, i ) => {return i !== index;} );
    setFiles( next );
    onFilesChange?.( next );

    if ( inputRef.current ) {inputRef.current.value = '';}
  };

  return (
    <>
      <label className='text-gray-600'>
        {label ?? t( 'common:drop_files' )}
      </label>

      <div
        className={`h-auto min-h-60 rounded-xl text-black relative border-2 border-dashed flex flex-col items-center justify-center w-full border-orange-base-light trans-fast p-4 gap-4 ${
          isDragging ? 'bg-orange-100' : 'hover:bg-orange-100'
        }`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        <div className="flex-center flex-col gap-4">
          <div className="font-bold text-xl font-texte">
            {multipleFiles ? t( 'common:drop_files' ) : t( 'common:drop_file' )}
          </div>

          <div>{t( 'common:or' )}</div>

          <label className="cursor-pointer flex-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl shadow-md hover:bg-orange-600 transition">
            <FileIcon size={7} />
            {t( 'common:browse' )}...

            <input
              type="file"
              ref={inputRef}
              onChange={handleChange}
              disabled={disable}
              multiple={multipleFiles}
              accept={acceptedFormats.join( ',' )}

              className="hidden"
            />
          </label>
        </div>

        {files.length > 0 && (
          <div className="w-full mt-4 space-y-2">
            {files.map( ( file, index ) => {return (
              <div
                key={index}
                className="flex items-center justify-between p-2 rounded-md bg-white shadow text-sm"
              >
                <span className="truncate max-w-[70%]">
                  { file.isDirectory ? file.file.name.split( '.' ).slice( 0, -1 ).join( '.' ) : file.file.name }
                </span>

                <div className="flex items-center gap-2">
                  <div className='flex gap-1'>
                    <span className='text-gray-500'>
                      { CommonDocumentService.getReadableFileSizeString( file.file.size / 1024 ) }
                    </span>
                    <span className='text-[12px] text-gray-400'>
                      { file.isDirectory ? '(' + t( 'common:compressed_size' ) + ')' : '' }
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {return handleRemove( index );}}
                    className="text-red-600 trans-fast rounded-xl p-2 hover:bg-red-100"
                  >
                    <TrashIcon size={5} />
                  </button>
                </div>
              </div>
            );} )}
          </div>
        )}
      </div>
    </>
  );
};

export default SendFileInput;